import { forwardRef, useMemo } from 'react';
import type { OrderItem } from '../../../../types';
import type { ExportConfig } from './exportTypes';
import { itemsDailySubtotal, paymentStatusOf, paymentStatusLabel, pendingBalanceOf, roundMoney } from '../../../../utils/paymentAmount';
import { bookingDays, todayString, toLocalDateString } from '../../../../utils/dateUtils';

interface OrderItemDetail {
  id: string;
  itemName: string | null;
  itemSpec: string | null;
  quantity: number | null;
  unitPrice: number | null;
}

interface Props {
  order: OrderItem;
  orderItems: OrderItemDetail[];
  config: ExportConfig;
}

/**
 * 导出卡片的付款状态：判定走 web 端唯一实现 `paymentStatusOf`，0 元单映射「无金额」。
 * 三个取值就是 `orders.payment_status` 这一 PG 枚举的原值（未付款/部分付款/已结清），
 * 不另造「待付款」「已付款」这类同义说法——打印件与列表、筛选、Excel 台账必须是同一套词，
 * 否则用户在同一条数据上见到两个名字，就会怀疑是两个不同的状态。
 * 「已退款」同样不在其中：退款只在会员订单与支付流水上有独立语义，从来不会出现在这一列。
 *
 * `paymentStatusLabel` 的第二参必须传**已 Number 化**的 total：直接传原始的 null 会让
 * 一张没带金额的订单从「无金额」翻成「未付款」，而这两种说法在对外打印件上是两回事。
 */
function calcPaymentStatus(orderAmount: number | null, paidAmount: number | null): string {
  const total = Number(orderAmount ?? 0);
  // 导出卡片是对外物，0 元单（先占档期后定价）写「未付款 + 零待收」是自相矛盾 → 映射「无金额」。
  // 与后端 buildOrderSummary 同口径；本函数只产展示文案，不参与任何判定。
  return paymentStatusLabel(paymentStatusOf(total, Number(paidAmount ?? 0)), total);
}

/** 待收逐单夹零：实收允许大于订单金额（预收/挂账），多收的那张单待收记 0，不印成负数 */
function pendingOf(order: OrderItem): number {
  return pendingBalanceOf(Number(order.orderAmount ?? 0), Number(order.paidAmount ?? 0));
}

/**
 * 打印件上的日期一律原样取串，不交 `new Date()` 按浏览器时区重解，也不走
 * `toLocaleDateString('zh-CN')`：那个格式全站不补零（实测东八区给 `2026/10/9`），
 * 而同一张纸上的预约日期是 `2026-10-09`，两种写法并排就像两张单子拼起来的。
 * 生成日期走 `todayString()`（本地日历日，补零），纯日期列走 `fmtDate`。
 */
const fmtDate = (d: string | null) => toLocalDateString(d) ?? '-';

/**
 * 到达时间是库里约定的**naive 墙上时间**（`YYYY-MM-DDTHH:mm`，见 docs/接口契约.md），
 * 过了 `new Date()` 就要按浏览器时区再解一次，打印件要的却是当时那个钟点。
 * 所以这里只取串换分隔符，与台账、详情页同一个值逐字一致。
 */
const fmtArrival = (d: string | null) => (d ? d.replace('T', ' ').slice(5) : '-');

/**
 * 明细三件套：每日小计 × 预约天数 = 整单合计。
 * 三张模板共用这一份，算式与后端 `order-amount-guard` 一致——打印件是对外的凭据，
 * 少乘一次天数就是「纸面金额」跟「订单金额」自相矛盾。
 */
function useItemsAmount(order: OrderItem, orderItems: OrderItemDetail[]) {
  const perDay = useMemo(() => itemsDailySubtotal(orderItems), [orderItems]);
  const numDays = useMemo(
    () => bookingDays(order.orderStartDate, order.orderEndDate),
    [order.orderStartDate, order.orderEndDate],
  );
  // numDays 为 null 只在缺预约日期的历史脏单上出现：按单日不乘（乘 0 会印出一张 0 元单）
  const itemsTotal = useMemo(() => roundMoney(perDay * (numDays ?? 1)), [perDay, numDays]);
  return { perDay, numDays, itemsTotal };
}

/** 合计行左侧文案：多天单把乘式写全，量纲一目了然；单日单不啰嗦 */
function itemsTotalNote(count: number, perDay: number, numDays: number | null): string {
  if (numDays === null || numDays <= 1) return `共 ${count} 项`;
  return `共 ${count} 项 · 每日 ¥${perDay.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} × ${numDays} 天 =`;
}

/* ──────────────────────────────────── Templates ── */

/** 简约白 */
function MinimalTemplate({ order, orderItems, config }: Props) {
  const { sections, customInfo, showWatermark } = config;
  const payStatus = useMemo(() => calcPaymentStatus(order.orderAmount, order.paidAmount), [order.orderAmount, order.paidAmount]);
  const { perDay, numDays, itemsTotal } = useItemsAmount(order, orderItems);

  return (
    <div style={{ width: '794px', minHeight: '1123px', background: '#fff', fontFamily: '\'Noto Sans SC\', sans-serif', position: 'relative', padding: '56px 60px 48px' }}>
      {showWatermark && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 0, transform: 'rotate(-30deg)', opacity: 0.05, fontSize: 80, fontWeight: 700, color: '#000', letterSpacing: 8 }}>
          仅供内部使用
        </div>
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0d9488', paddingBottom: 20, marginBottom: 28 }}>
          <div>
            {customInfo.companyName && (
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0d9488', marginBottom: 4 }}>{customInfo.companyName}</div>
            )}
            {customInfo.tagline && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{customInfo.tagline}</div>
            )}
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: customInfo.companyName ? 0 : 0 }}>订单凭证</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', fontFamily: 'monospace', letterSpacing: 1 }}>
              #{order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>开始日期：{fmtDate(order.orderStartDate)}</div>
            {order.createdAt && (
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>创建：{fmtDate(order.createdAt)}</div>
            )}
          </div>
        </div>

        {/* Status badges */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>
            {order.orderStatus ?? '-'}
          </span>
          <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: '#f0fdfa', color: '#0d9488', border: '1px solid #99f6e4' }}>
            {payStatus}
          </span>
          {order.orderCategory && (
            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb' }}>
              {order.orderCategory}
            </span>
          )}
          {order.serviceType?.name && (
            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb' }}>
              {order.serviceType.name}
            </span>
          )}
        </div>

        {/* Amount summary */}
        {order.orderAmount != null && (
          <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>订单总额</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#0d9488' }}>
                ¥{Number(order.orderAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 32 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>已收款</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>¥{Number(order.paidAmount ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>待收余款</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: pendingOf(order) > 0 ? '#d97706' : '#374151' }}>
                  ¥{pendingOf(order).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </div>
              </div>
              {order.paymentMethod && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>付款方式</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{order.paymentMethod}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Two columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {/* Customer info */}
          {sections.customerInfo && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: '#f9fafb', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>客户信息</div>
              <div style={{ padding: '12px 14px' }}>
                {order.customer && (
                  <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{order.customer.fullName}</div>
                    {order.customer.companyName && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{order.customer.companyName}</div>}
                  </div>
                )}
                {[
                  { label: '联系人', value: order.contactPerson },
                  { label: '联系电话', value: order.contactPhone },
                  { label: '活动地址', value: order.activityAddress },
                ].filter(r => r.value).map(row => (
                  <div key={row.label} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 5 }}>
                    <span style={{ color: '#9ca3af', minWidth: 52 }}>{row.label}</span>
                    <span style={{ color: '#374151' }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Schedule */}
          {sections.schedule && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: '#f9fafb', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>时间安排</div>
              <div style={{ padding: '12px 14px' }}>
                {/* 服务时长不印：它没有对应列，服务端投影恒为 null（裁定＝只做录入页实时提示，不传后台），
                    打印件是对外的凭据，留一个永远为空的行等于宣称「我们记录了时长」 */}
                {[
                  { label: '开始日期', value: fmtDate(order.orderStartDate) },
                  ...(order.orderEndDate && order.orderEndDate !== order.orderStartDate ? [{ label: '结束日期', value: fmtDate(order.orderEndDate) }] : []),
                  { label: '服务时间', value: order.isFullDay ? '全天' : `${order.orderTimeStart?.slice(0, 5) ?? '-'}${order.orderTimeEnd ? ' - ' + order.orderTimeEnd.slice(0, 5) : ''}` },
                  ...(order.arrivalDateTime ? [{ label: '预计到达', value: fmtArrival(order.arrivalDateTime) }] : []),
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 5 }}>
                    <span style={{ color: '#9ca3af', minWidth: 52 }}>{row.label}</span>
                    <span style={{ color: '#374151' }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Order items */}
        {sections.orderItems && orderItems.length > 0 && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ background: '#f9fafb', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>订单明细</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f3f4f6' }}>
                  {['名称', '规格', '数量', '每日单价', '每日小计'].map((h, i) => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: i >= 2 ? 'center' : 'left', color: '#6b7280', fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, idx) => {
                  const subtotal = roundMoney(Number(item.quantity ?? 0) * Number(item.unitPrice ?? 0));
                  return (
                    <tr key={item.id ?? idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 14px', color: '#111827', fontWeight: 500 }}>{item.itemName ?? '-'}</td>
                      <td style={{ padding: '8px 14px', color: '#6b7280' }}>{item.itemSpec || '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#374151' }}>{item.quantity ?? '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#374151' }}>¥{Number(item.unitPrice ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#111827', fontWeight: 600 }}>¥{subtotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #e5e7eb', background: '#fafafa' }}>
                  <td colSpan={4} style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280' }}>{itemsTotalNote(orderItems.length, perDay, numDays)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#0d9488', fontWeight: 700, fontSize: 14 }}>¥{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Payment detail */}
        {sections.payment && (order.paymentMethod || order.paymentDate) && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ background: '#f9fafb', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>付款详情</div>
            <div style={{ padding: '12px 14px', display: 'flex', gap: 32 }}>
              {order.paymentMethod && (
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>付款方式</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{order.paymentMethod}</div>
                </div>
              )}
              {order.paymentDate && (
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>付款日期</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{fmtDate(order.paymentDate)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Special instructions */}
        {sections.specialInstructions && order.orderSpecialInstructions && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>特殊说明</div>
            <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.7 }}>{order.orderSpecialInstructions}</div>
          </div>
        )}

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 10, color: '#d1d5db', fontFamily: 'monospace' }}>ID: {order.id}</div>
          {customInfo.footerNote ? (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{customInfo.footerNote}</div>
          ) : (
            <div style={{ fontSize: 10, color: '#d1d5db' }}>{todayString()} 生成</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** 商务深色 */
function BusinessTemplate({ order, orderItems, config }: Props) {
  const { sections, customInfo, showWatermark } = config;
  const payStatus = useMemo(() => calcPaymentStatus(order.orderAmount, order.paidAmount), [order.orderAmount, order.paidAmount]);
  const { perDay, numDays, itemsTotal } = useItemsAmount(order, orderItems);

  return (
    <div style={{ width: '794px', minHeight: '1123px', background: '#fff', fontFamily: '\'Noto Sans SC\', sans-serif', position: 'relative' }}>
      {showWatermark && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 0, transform: 'rotate(-30deg)', opacity: 0.04, fontSize: 80, fontWeight: 700, color: '#000', letterSpacing: 8 }}>
          仅供内部使用
        </div>
      )}
      {/* Header bar */}
      <div style={{ background: '#1e293b', padding: '32px 52px 28px', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            {customInfo.companyName ? (
              <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>{customInfo.companyName}</div>
            ) : (
              <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', letterSpacing: 2, textTransform: 'uppercase' }}>Order Document</div>
            )}
            {customInfo.tagline && <div style={{ fontSize: 11, color: '#64748b' }}>{customInfo.tagline}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', fontFamily: 'monospace', letterSpacing: 2 }}>
              #{order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{fmtDate(order.orderStartDate)}</div>
          </div>
        </div>

        {order.orderAmount != null && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>TOTAL AMOUNT</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9' }}>¥{Number(order.orderAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              {[
                { label: '已收款', value: `¥${Number(order.paidAmount ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}` },
                { label: '待收款', value: `¥${pendingOf(order).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}` },
                ...(order.paymentMethod ? [{ label: '付款方式', value: order.paymentMethod }] : []),
              ].map(item => (
                <div key={item.label} style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>{item.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#cbd5e1' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '28px 52px 40px', position: 'relative', zIndex: 1 }}>
        {/* Status */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[order.orderStatus, payStatus, order.orderCategory, order.serviceType?.name].filter(Boolean).map(label => (
            <span key={label} style={{ padding: '4px 12px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{label}</span>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {sections.customerInfo && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>CUSTOMER</div>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                {order.customer && <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>{order.customer.fullName}{order.customer.companyName ? ` · ${order.customer.companyName}` : ''}</div>}
                {[
                  { l: '联系人', v: order.contactPerson }, { l: '电话', v: order.contactPhone }, { l: '地址', v: order.activityAddress },
                ].filter(r => r.v).map(r => (
                  <div key={r.l} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#94a3b8', minWidth: 40 }}>{r.l}</span>
                    <span style={{ color: '#475569' }}>{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sections.schedule && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>SCHEDULE</div>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                {[
                  { l: '开始', v: fmtDate(order.orderStartDate) },
                  ...(order.orderEndDate && order.orderEndDate !== order.orderStartDate ? [{ l: '结束', v: fmtDate(order.orderEndDate) }] : []),
                  { l: '时间', v: order.isFullDay ? '全天' : `${order.orderTimeStart?.slice(0, 5) ?? '-'}${order.orderTimeEnd ? ' - ' + order.orderTimeEnd.slice(0, 5) : ''}` },
                ].map(r => (
                  <div key={r.l} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#94a3b8', minWidth: 40 }}>{r.l}</span>
                    <span style={{ color: '#475569' }}>{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {sections.orderItems && orderItems.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>ORDER ITEMS</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#1e293b', color: '#e2e8f0' }}>
                  {['名称', '规格', '数量', '每日单价', '每日小计'].map((h, i) => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: i >= 2 ? 'center' : 'left', fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, idx) => {
                  const subtotal = roundMoney(Number(item.quantity ?? 0) * Number(item.unitPrice ?? 0));
                  return (
                    <tr key={item.id ?? idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 14px', color: '#1e293b', fontWeight: 500 }}>{item.itemName ?? '-'}</td>
                      <td style={{ padding: '8px 14px', color: '#64748b' }}>{item.itemSpec || '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#475569' }}>{item.quantity ?? '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#475569' }}>¥{Number(item.unitPrice ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 700, color: '#1e293b' }}>¥{subtotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#1e293b' }}>
                  <td colSpan={4} style={{ padding: '10px 14px', fontSize: 11, color: '#64748b' }}>{itemsTotalNote(orderItems.length, perDay, numDays)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f1f5f9', fontWeight: 700, fontSize: 14 }}>¥{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {sections.specialInstructions && order.orderSpecialInstructions && (
          <div style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#713f12', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>NOTES</div>
            <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.7 }}>{order.orderSpecialInstructions}</div>
          </div>
        )}

        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <div style={{ fontSize: 10, color: '#cbd5e1', fontFamily: 'monospace' }}>ID: {order.id}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{customInfo.footerNote || todayString() + ' 生成'}</div>
        </div>
      </div>
    </div>
  );
}

/** 暖色调 */
function WarmTemplate({ order, orderItems, config }: Props) {
  const { sections, customInfo, showWatermark } = config;
  const payStatus = useMemo(() => calcPaymentStatus(order.orderAmount, order.paidAmount), [order.orderAmount, order.paidAmount]);
  const { perDay, numDays, itemsTotal } = useItemsAmount(order, orderItems);

  return (
    <div style={{ width: '794px', minHeight: '1123px', background: '#fffbf5', fontFamily: '\'Noto Sans SC\', sans-serif', position: 'relative' }}>
      {showWatermark && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 0, transform: 'rotate(-30deg)', opacity: 0.05, fontSize: 80, fontWeight: 700, color: '#000', letterSpacing: 8 }}>仅供内部使用</div>
      )}
      {/* Decorative top stripe */}
      <div style={{ height: 6, background: 'linear-gradient(90deg, #f59e0b, #f97316, #ef4444)' }} />

      <div style={{ padding: '40px 52px 40px', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            {customInfo.companyName && <div style={{ fontSize: 22, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>{customInfo.companyName}</div>}
            {customInfo.tagline && <div style={{ fontSize: 11, color: '#b45309', marginBottom: 4 }}>{customInfo.tagline}</div>}
            <div style={{ fontSize: 11, color: '#d97706', fontWeight: 500 }}>订单凭证</div>
          </div>
          <div style={{ textAlign: 'right', background: '#fef3c7', borderRadius: 10, padding: '10px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', fontFamily: 'monospace', letterSpacing: 1 }}>
              #{order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>{fmtDate(order.orderStartDate)}</div>
            {order.createdAt && <div style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}>{fmtDate(order.createdAt)}</div>}
          </div>
        </div>

        {/* Amount */}
        {order.orderAmount != null && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#b45309', marginBottom: 4 }}>订单总额</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#92400e' }}>¥{Number(order.orderAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
            </div>
            <div style={{ display: 'flex', gap: 28 }}>
              <div>
                <div style={{ fontSize: 11, color: '#b45309', marginBottom: 2 }}>已收款</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#78350f' }}>¥{Number(order.paidAmount ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#b45309', marginBottom: 2 }}>待收款</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: pendingOf(order) > 0 ? '#dc2626' : '#78350f' }}>
                  ¥{pendingOf(order).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Badges */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {[order.orderStatus, payStatus, order.orderCategory, order.serviceType?.name].filter(Boolean).map(label => (
            <span key={label} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>{label}</span>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {sections.customerInfo && (
            <div style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>客户信息</div>
              {order.customer && <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1917', marginBottom: 6 }}>{order.customer.fullName}{order.customer.companyName ? ` · ${order.customer.companyName}` : ''}</div>}
              {[{ l: '联系人', v: order.contactPerson }, { l: '电话', v: order.contactPhone }, { l: '地址', v: order.activityAddress }].filter(r => r.v).map(r => (
                <div key={r.l} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: '#d97706', minWidth: 40 }}>{r.l}</span>
                  <span style={{ color: '#57534e' }}>{r.v}</span>
                </div>
              ))}
            </div>
          )}
          {sections.schedule && (
            <div style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>时间安排</div>
              {[
                { l: '开始', v: fmtDate(order.orderStartDate) },
                ...(order.orderEndDate && order.orderEndDate !== order.orderStartDate ? [{ l: '结束', v: fmtDate(order.orderEndDate) }] : []),
                { l: '时间', v: order.isFullDay ? '全天' : `${order.orderTimeStart?.slice(0, 5) ?? '-'}${order.orderTimeEnd ? ' - ' + order.orderTimeEnd.slice(0, 5) : ''}` },
              ].map(r => (
                <div key={r.l} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: '#d97706', minWidth: 40 }}>{r.l}</span>
                  <span style={{ color: '#57534e' }}>{r.v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {sections.orderItems && orderItems.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>订单明细</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fef3c7' }}>
                  {['名称', '规格', '数量', '每日单价', '每日小计'].map((h, i) => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: i >= 2 ? 'center' : 'left', color: '#92400e', fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, idx) => {
                  const subtotal = roundMoney(Number(item.quantity ?? 0) * Number(item.unitPrice ?? 0));
                  return (
                    <tr key={item.id ?? idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fffbf5', borderBottom: '1px solid #fde68a' }}>
                      <td style={{ padding: '8px 14px', color: '#1c1917', fontWeight: 500 }}>{item.itemName ?? '-'}</td>
                      <td style={{ padding: '8px 14px', color: '#78716c' }}>{item.itemSpec || '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#57534e' }}>{item.quantity ?? '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', color: '#57534e' }}>¥{Number(item.unitPrice ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 700, color: '#92400e' }}>¥{subtotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#fef3c7' }}>
                  <td colSpan={4} style={{ padding: '10px 14px', fontSize: 11, color: '#b45309' }}>{itemsTotalNote(orderItems.length, perDay, numDays)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#92400e', fontWeight: 700, fontSize: 14 }}>¥{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {sections.specialInstructions && order.orderSpecialInstructions && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9a3412', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>特殊说明</div>
            <div style={{ fontSize: 12, color: '#7c2d12', lineHeight: 1.7 }}>{order.orderSpecialInstructions}</div>
          </div>
        )}

        <div style={{ borderTop: '1px solid #fde68a', paddingTop: 14, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 10, color: '#d6d3d1', fontFamily: 'monospace' }}>ID: {order.id}</div>
          <div style={{ fontSize: 11, color: '#b45309' }}>{customInfo.footerNote || todayString() + ' 生成'}</div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────── Export entry ── */
const OrderExportPreview = forwardRef<HTMLDivElement, Props>(
  function OrderExportPreview(props, ref) {
    return (
      <div ref={ref}>
        {props.config.template === 'minimal' && <MinimalTemplate {...props} />}
        {props.config.template === 'business' && <BusinessTemplate {...props} />}
        {props.config.template === 'warm' && <WarmTemplate {...props} />}
      </div>
    );
  }
);

export default OrderExportPreview;
